import { useEffect, useState } from 'react';
import {
  CheckCircleOutlined,
  DescriptionOutlined,
  DownloadOutlined,
  PictureAsPdfOutlined,
} from '@mui/icons-material';
import { Alert, Box, Button, Divider, MenuItem, Stack, Typography } from '@mui/material';
import {
  FormTextField,
  GuidePage,
  PageSections,
  PillTabs,
  ResourceState,
  SectionCard,
  SegmentedControl,
  StatusBadge,
  ValueBadge,
  designTokens,
} from '../../../design-system';
import { CertificateSheet } from '../components/CertificateSheet';
import {
  certificateDocuments,
  certificatePreview,
  certificateRequests,
  certificateTemplates,
} from '../fixtures/certificateFixtures';
import {
  certificateIssueError,
  certificatePurposeOptions,
  certificateStatusLabel,
  certificateStatusTone,
  displayCertificateDate,
} from '../model/presentation';
import type {
  CertificateAccessProps,
  CertificateIssueInput,
  CertificateRequest,
} from '../model/types';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import type { CertificateSelfServicePort } from '../port/CertificatesPort';

export function CertificateSelfServicePage({
  port,
  fixtureMode,
  readOnly,
  scopes,
}: {
  port: CertificateSelfServicePort;
  fixtureMode: boolean;
} & CertificateAccessProps) {
  const canIssue = !readOnly && scopes.includes('certificates.request.self');
  const canDownload = !readOnly && scopes.includes('documents.read');
  const [tab, setTab] = useState<'issue' | 'requests' | 'documents'>('issue');
  const templates = useAsyncResource(
    () => fixtureMode ? Promise.resolve(certificateTemplates) : port.listAvailableCertificateTypes(),
    [port, fixtureMode],
  );
  const requests = useAsyncResource(
    () => fixtureMode ? Promise.resolve(certificateRequests) : port.listMyCertificateRequests(),
    [port, fixtureMode],
  );
  const documents = useAsyncResource(
    () => fixtureMode ? Promise.resolve(certificateDocuments) : port.listDocuments(),
    [port, fixtureMode],
  );
  const [selectedType, setSelectedType] = useState<CertificateIssueInput['certificateType']>('EMPLOYMENT');
  const preview = useAsyncResource(
    () => fixtureMode
      ? Promise.resolve({
        ...certificatePreview,
        template: certificateTemplates.find((item) => item.certificateType === selectedType) ?? certificateTemplates[0],
      })
      : port.previewCertificate(selectedType),
    [port, fixtureMode, selectedType],
  );
  const [purposeCode, setPurposeCode] = useState<CertificateIssueInput['purposeCode']>('FINANCIAL_INSTITUTION');
  const [destination, setDestination] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [issueError, setIssueError] = useState<string>();
  const [issued, setIssued] = useState<CertificateRequest>();

  useEffect(() => {
    if (templates.data?.length && !templates.data.some((item) => item.certificateType === selectedType)) {
      setSelectedType(templates.data[0].certificateType);
    }
  }, [templates.data, selectedType]);

  useEffect(() => {
    setIssued(undefined);
    setIssueError(undefined);
  }, [destination, purposeCode, selectedType]);

  const download = (documentId: string, filename: string) => {
    if (!canDownload || fixtureMode) return;
    void port.downloadDocument(documentId).then((blob) => {
      const objectURL = URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');
      anchor.href = objectURL;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(objectURL);
    }, (reason: unknown) => {
      setIssueError(reason instanceof Error ? reason.message : '문서를 다운로드하지 못했습니다.');
    });
  };

  const issue = () => {
    if (!canIssue) return;
    setIssuing(true);
    setIssueError(undefined);
    setIssued(undefined);
    const request: Promise<CertificateRequest> = fixtureMode
      ? Promise.resolve({
        ...certificateRequests[0],
        id: crypto.randomUUID(),
        certificateType: selectedType,
        requestedAt: new Date().toISOString(),
        displayName: preview.data?.template.displayName ?? '증명서',
      })
      : port.issueCertificate({
        certificateType: selectedType,
        purposeCode,
        destination: destination.trim() || undefined,
      });
    void request.then((item) => {
      setIssued(item);
      void requests.reload();
      void documents.reload();
    }, (reason: unknown) => {
      setIssueError(certificateIssueError(reason));
      void requests.reload();
    }).finally(() => setIssuing(false));
  };

  const fillEmptySpace = tab === 'issue' ? !templates.data?.length
    : tab === 'requests' ? !requests.data?.length : !documents.data?.length;
  const emptyPanelLayout = fillEmptySpace
    ? { height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' } as const
    : undefined;
  const refreshCurrentTab = () => {
    if (tab === 'issue') { templates.reload(); preview.reload(); }
    else if (tab === 'requests') requests.reload();
    else documents.reload();
  };
  const refreshing = tab === 'issue' ? templates.loading || preview.loading
    : tab === 'requests' ? requests.loading : documents.loading;
  return <GuidePage
    title="문서·증명서"
    fill={fillEmptySpace}
    refreshAction={{ onClick: refreshCurrentTab, loading: refreshing, disabled: issuing }}
    tabs={<PillTabs
      value={tab}
      onChange={setTab}
      ariaLabel="문서와 증명서 메뉴"
      items={[
        { value: 'issue', label: '증명서 발급', tabId: 'certificate-issue-tab', panelId: 'certificate-issue-panel' },
        { value: 'requests', label: `발급 내역 ${requests.data?.length ?? 0}`, tabId: 'certificate-requests-tab', panelId: 'certificate-requests-panel' },
        { value: 'documents', label: `내 문서 ${documents.data?.length ?? 0}`, tabId: 'certificate-documents-tab', panelId: 'certificate-documents-panel' },
      ]}
    />}
  >
    <PageSections fill={fillEmptySpace}>
    <Box data-testid="certificate-tab-panels" sx={fillEmptySpace ? { flex: 1, minHeight: 0 } : undefined}>
      <Box id="certificate-issue-panel" role="tabpanel" aria-labelledby="certificate-issue-tab" hidden={tab !== 'issue'} sx={tab === 'issue' ? emptyPanelLayout : undefined}>
        {tab === 'issue' && <ResourceState embedded fill={fillEmptySpace} loading={templates.loading || preview.loading} error={templates.error?.message ?? preview.error?.message} empty={!templates.data?.length} emptyAction={false} onRetry={() => { void templates.reload(); void preview.reload(); }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(320px, 0.8fr) minmax(440px, 1.2fr)' }, gap: `${designTokens.layout.pageSectionGap}px`, alignItems: 'start' }}>
            <PageSections>
              <SectionCard title="발급 정보" help="표준 증명서는 승인 없이 즉시 발급됩니다. 원장 정보가 부족하면 확인할 항목을 안내합니다. 발급본에는 발급번호와 외부 진위확인 QR 코드가 포함됩니다." bodyPadding="standard">
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>증명서 종류</Typography>
                    <SegmentedControl
                      ariaLabel="증명서 종류"
                      value={selectedType}
                      onChange={setSelectedType}
                      disabled={issuing}
                      items={(templates.data ?? []).map((template) => ({ value: template.certificateType, label: template.displayName }))}
                    />
                  </Box>
                  <FormTextField select label="용도" value={purposeCode} disabled={!canIssue || issuing} onChange={(event) => setPurposeCode(event.target.value as CertificateIssueInput['purposeCode'])}>
                    {certificatePurposeOptions.map((option) => <MenuItem key={option.value} value={option.value}><ValueBadge label={option.label} /></MenuItem>)}
                  </FormTextField>
                  <FormTextField label="제출처" value={destination} disabled={!canIssue || issuing} onChange={(event) => setDestination(event.target.value)} placeholder="예: 소카은행" slotProps={{ htmlInput: { maxLength: 120 } }} help="입력하면 ‘소카은행 제출용’처럼 증명서 용도에 표시되고 발급 내역에도 기록됩니다." />
                  {issueError && <Alert severity="error">{issueError}</Alert>}
                  {issued?.issuance && <Stack spacing={1}>
                    <Alert severity="success" icon={<CheckCircleOutlined />}>증명서를 발급했습니다. 발급번호 {issued.issuance.issuanceNumber}</Alert>
                    {canDownload && <Button variant="outlined" startIcon={<DownloadOutlined />} sx={{ alignSelf: { sm: 'flex-start' } }} onClick={() => download(issued.issuance!.documentId, `${issued.displayName}.pdf`)}>발급한 PDF 다운로드</Button>}
                  </Stack>}
                  {canIssue && <Button variant="contained" size="large" startIcon={<PictureAsPdfOutlined />} disabled={issuing || !preview.data} onClick={issue}>{issuing ? 'PDF 생성 중…' : '증명서 발급하기'}</Button>}
                </Stack>
              </SectionCard>
            </PageSections>
            <Box sx={{ bgcolor: designTokens.color.subtleSurface, borderRadius: designTokens.radius.surface, p: { xs: 1.5, sm: 3 }, minHeight: 560 }}>
              {preview.data && <CertificateSheet preview={preview.data} purposeCode={purposeCode} destination={destination} />}
            </Box>
          </Box>
        </ResourceState>}
      </Box>
      <Box id="certificate-requests-panel" role="tabpanel" aria-labelledby="certificate-requests-tab" hidden={tab !== 'requests'} sx={tab === 'requests' ? emptyPanelLayout : undefined}>
        {tab === 'requests' && <ResourceState embedded fill={fillEmptySpace} loading={requests.loading} error={requests.error?.message} empty={!requests.data?.length} emptyAction={false} onRetry={requests.reload}>
          <SectionCard title="발급 내역" headerPadding="compact"><Stack divider={<Divider />}>{requests.data?.map((item) => <Stack key={item.id} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ px: `${designTokens.layout.sectionCardBodyPadding}px`, py: 1.5, alignItems: { sm: 'center' } }}>
            <PictureAsPdfOutlined sx={{ color: designTokens.color.danger }} /><Box sx={{ flex: 1 }}><Typography variant="body2" sx={{ fontWeight: 720 }}>{item.displayName}</Typography><Typography variant="caption" color="text.secondary">{displayCertificateDate(item.requestedAt)} · {item.destination || certificatePurposeOptions.find((option) => option.value === item.purposeCode)?.label}</Typography></Box>
            <StatusBadge tone={certificateStatusTone(item.status)} label={certificateStatusLabel(item.status)} />
            {canDownload && item.issuance?.documentId && <Button size="small" startIcon={<DownloadOutlined />} onClick={() => download(item.issuance!.documentId, `${item.displayName}.pdf`)}>다운로드</Button>}
          </Stack>)}</Stack></SectionCard>
        </ResourceState>}
      </Box>
      <Box id="certificate-documents-panel" role="tabpanel" aria-labelledby="certificate-documents-tab" hidden={tab !== 'documents'} sx={tab === 'documents' ? emptyPanelLayout : undefined}>
        {tab === 'documents' && <ResourceState embedded fill={fillEmptySpace} loading={documents.loading} error={documents.error?.message} empty={!documents.data?.length} emptyAction={false} onRetry={documents.reload}>
          <SectionCard title="내 문서" headerPadding="compact"><Stack divider={<Divider />}>{documents.data?.map((document) => <Stack key={document.id} direction="row" spacing={1.25} sx={{ px: `${designTokens.layout.sectionCardBodyPadding}px`, py: 1.5, alignItems: 'center' }}>
            <DescriptionOutlined color="action" /><Box sx={{ flex: 1, minWidth: 0 }}><Typography variant="body2" noWrap sx={{ fontWeight: 720 }}>{document.displayName}</Typography><Typography variant="caption" color="text.secondary"><ValueBadge label={document.documentType} size="compact" /> · {displayCertificateDate(document.createdAt)}</Typography></Box>
            <StatusBadge tone={certificateStatusTone(document.scanStatus)} label={document.scanStatus === 'CLEAN' ? '다운로드 가능' : '검사 중'} />
            {canDownload && document.scanStatus === 'CLEAN' && <Button size="small" startIcon={<DownloadOutlined />} onClick={() => download(document.id, document.displayName)}>다운로드</Button>}
          </Stack>)}</Stack></SectionCard>
        </ResourceState>}
      </Box>
    </Box>
    </PageSections>
  </GuidePage>;
}
